"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HWProvider = void 0;
const hw_transport_webusb_1 = __importDefault(require("@ledgerhq/hw-transport-webusb"));
const hw_transport_webhid_1 = __importDefault(require("@ledgerhq/hw-transport-webhid"));
const hw_transport_u2f_1 = __importDefault(require("@ledgerhq/hw-transport-u2f"));
const ledgerApp_1 = __importDefault(require("./ledgerApp"));
const platform_1 = __importDefault(require("platform"));
const versioning_1 = require("./versioning");
const constants_1 = require("./constants");
const signature_1 = require("./signature");
const userAddress_1 = require("./userAddress");
const transactionVersion_1 = require("./transactionVersion");
const transactionOptions_1 = require("./transactionOptions");
const errors_1 = require("./errors");
class HWProvider {
    constructor() {
        this.addressIndex = 0;
    }
    /**
     * Creates transport and initialises ledger app.
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const transport = yield this.getTransport();
                this.hwApp = new ledgerApp_1.default(transport);
                return true;
            }
            catch (error) {
                return false;
            }
        });
    }
    getTransport() {
        return __awaiter(this, void 0, void 0, function* () {
            let webUSBSupported = yield hw_transport_webusb_1.default.isSupported();
            webUSBSupported =
                webUSBSupported &&
                    platform_1.default.name !== "Opera";
            if (webUSBSupported) {
                return yield hw_transport_webusb_1.default.create();
            }
            let webHIDSupported = yield hw_transport_webhid_1.default.isSupported();
            if (webHIDSupported) {
                return yield hw_transport_webhid_1.default.open("");
            }
            return yield hw_transport_u2f_1.default.create();
        });
    }
    /**
     * Returns true if init() was previously called successfully
     */
    isInitialized() {
        return !!this.hwApp;
    }
    /**
     * Mocked function, returns isInitialized as an async function
     */
    isConnected() {
        return new Promise((resolve, _) => resolve(this.isInitialized()));
    }
    /**
     * Performs a login request by setting the selected index in Ledger and returning that address
     */
    login(options = { addressIndex: 0 }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            yield this.setAddressIndex(options.addressIndex);
            const { address } = yield this.hwApp.getAddress(0, options.addressIndex, true);
            return address;
        });
    }
    setAddressIndex(addressIndex) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            this.addressIndex = addressIndex;
            yield this.hwApp.setAddress(0, addressIndex);
        });
    }
    getAccounts(page = 0, pageSize = 10) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            const addresses = [];
            const startIndex = page * pageSize;
            for (let index = startIndex; index < startIndex + pageSize; index++) {
                const { address } = yield this.hwApp.getAddress(0, index);
                addresses.push(address);
            }
            return addresses;
        });
    }
    /**
     * Mocks a logout request by returning true
     */
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            return true;
        });
    }
    /**
     * Fetches current selected ledger address
     */
    getAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            const { address } = yield this.hwApp.getAddress(0, this.addressIndex);
            return address;
        });
    }
    signTransaction(transaction) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            const currentAddressBech32 = yield this.getAddress();
            const currentAddress = new userAddress_1.UserAddress(currentAddressBech32);
            const signUsingHash = yield this.shouldSignUsingHash();
            if (signUsingHash) {
                transaction.options = transactionOptions_1.TransactionOptions.withTxHashSignOptions();
                transaction.version = transactionVersion_1.TransactionVersion.withTxHashSignVersion();
            }
            const serializedTransaction = transaction.serializeForSigning(currentAddress);
            const serializedTransactionBuffer = Buffer.from(serializedTransaction);
            const signature = yield this.hwApp.signTransaction(serializedTransactionBuffer, signUsingHash);
            transaction.applySignature(signature_1.Signature.fromHex(signature), currentAddress);
            return transaction;
        });
    }
    signTransactions(transactions) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const tx of transactions) {
                yield this.signTransaction(tx);
            }
            return transactions;
        });
    }
    signMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            let serializedMessage = message.serializeForSigningRaw();
            let serializedMessageBuffer = Buffer.from(serializedMessage);
            const signature = yield this.hwApp.signMessage(serializedMessageBuffer);
            message.applySignature(signature_1.Signature.fromHex(signature));
            return message;
        });
    }
    tokenLogin(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            let addressIndex = options.addressIndex || 0;
            yield this.setAddressIndex(addressIndex);
            const { signature, address } = yield this.hwApp.getAddressAndSignAuthToken(0, addressIndex, options.token);
            return {
                signature: signature_1.Signature.fromHex(signature),
                address
            };
        });
    }
    shouldSignUsingHash() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hwApp) {
                throw new errors_1.ErrNotInitialized();
            }
            const config = yield this.hwApp.getAppConfiguration();
            let diff = versioning_1.compareVersions(config.version, constants_1.LEDGER_TX_HASH_SIGN_MIN_VERSION);
            return diff >= 0;
        });
    }
}
exports.HWProvider = HWProvider;
